import { Component, useState } from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { filter, find, get, includes, isEmpty, map, some, sumBy } from "lodash";
import { push } from "store/actions/router";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { registerKYB } from "actions/adminActions";
import Button from "components/Button";
import { toast } from "react-toastify";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { wrapError } from "utils/errorHelper";
import { Link } from "react-router-dom";
import Alert from "components/Alert";

import {
  getCompany,
  getCompanyKybData,
  getCompanyMembers,
  removeCompanyMember,
} from "services/adminService";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { BsPencilSquare, BsTrash } from "react-icons/bs";

import InfoTable from "components/InfoTable";
import {
  BeneficialOwner,
  ControllingOfficer,
  ControllingOfficerAndBeneficialOwner,
  naicsValues,
} from "statics/businessCodes";
import IconSpinner from "components/IconSpinner";
import SimpleModal from "components/SimpleModal";
import {
  EmployerApplicationAdditionalDocumentationSubmitted,
  EmployerApplicationDenied,
  EmployerApplicationRequiresAdditionalDocumentation,
  EmployerApplicationSubmittedKYB,
} from "statics/states";
import KycJsonViewer from "pages/dashboards/adminDashboard/employers/KycJsonViewer";
import { isSuperIconAdmin } from "store/selectors/admin";
import AdvanceAlloyConfirmationModal from "pages/dashboards/adminDashboard/users/AdvanceAlloyConfirmationModal";
import type { RootState } from "types/state";

interface SsnLabelProps {
  ssn?: string;
}

const SsnLabel = ({ ssn = "" }: SsnLabelProps) => {
  const [visible, setVisible] = useState(false);

  const maskedSsn = `***-**-${ssn.substr(7)}`;

  const ssnToShow = visible ? ssn : maskedSsn;
  const icon = visible ? (
    <AiFillEyeInvisible size={16} onClick={() => setVisible(false)} />
  ) : (
    <AiFillEye size={16} onClick={() => setVisible(true)} />
  );

  return (
    <span>
      {ssnToShow}
      {icon}
    </span>
  );
};

/** Shape of an InfoTable metadata row (mirrors InfoTable's non-exported InfoTableMeta). */
interface MetadataRow {
  key?: string;
  label?: string;
  format?: string | ((data: unknown) => unknown);
  [key: string]: unknown;
}

/** Address fragment used by the company/member "Address" formatters. */
interface AddressLike {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

const companyMetadata: MetadataRow[] = [
  {
    key: "name",
    label: "Company Name",
  },
  {
    key: "dba",
    label: "DBA",
  },
  {
    key: "ein",
    label: "EIN",
  },
  {
    key: "type",
    label: "Incorporation Type",
  },
  {
    key: "naicsCode",
    label: "Category",
    format(data: unknown) {
      const code = data as string;
      const matchingCategory: Partial<(typeof naicsValues)[number]> =
        find(naicsValues, { value: code }) || {};
      return `${matchingCategory.category} - ${matchingCategory.description}`;
    },
  },
  {
    key: "address.0",
    label: "Address",
    format(data: unknown = {}) {
      const address = data as AddressLike;
      if (isEmpty(address)) {
        return "";
      }
      return `${address.address1}${
        address.address2 && ", " + address.address2
      }, ${address.city}, ${address.state} ${address.postalCode} `;
    },
  },
  {
    key: "address.0.phone",
    label: "Phone Number",
  },
  {
    key: "url",
    label: "Website",
  },
  {
    key: "silaWalletId",
    label: "Sila Wallet ID",
  },
];

const roleToEnglishMapping = {
  ControllingOfficer: "Controlling Officer",
  BeneficialOwner: "Beneficial Owner",
  ControllingOfficerAndBeneficialOwner:
    "Controlling Officer and Beneficial Owner",
};

const memberMetadata: MetadataRow[] = [
  {
    key: "memberProfile",
    label: "Name",
    format(data: unknown) {
      const profile = data as {
        firstName?: string | null;
        lastName?: string | null;
      };
      return `${profile.firstName} ${profile.lastName}`;
    },
  },
  {
    key: "memberProfile.email",
    label: "Email",
  },
  {
    key: "memberProfile.dob",
    label: "Date of Birth",
  },
  {
    key: "memberProfile.ssn",
    label: "SSN",
    format(data: unknown) {
      const ssn = data as string;
      if (ssn) {
        return <SsnLabel ssn={ssn} />;
      }

      return "";
    },
  },
  {
    key: "role",
    label: "Role",
    format(data: unknown) {
      const role = data as string;
      return roleToEnglishMapping[role as keyof typeof roleToEnglishMapping];
    },
  },
  {
    key: "ownershipStake",
    label: "Ownership Stake",
    format(data: unknown) {
      const stake = data as number;
      return stake;
    },
  },
  {
    key: "memberProfile",
    label: "Address",
    format(data: unknown = {}) {
      const address = data as AddressLike;
      if (isEmpty(address)) {
        return "";
      }
      return `${address.address1}${
        address.address2 && ", " + address.address2
      }, ${address.city}, ${address.state} ${address.postalCode} `;
    },
  },
  {
    key: "memberProfile.phoneNumber",
    label: "Phone Number",
  },
  {
    key: "id",
    label: "Member ID",
  },
];

/** A business member as returned by getCompanyMembers (services/adminService). */
type Member = NonNullable<
  NonNullable<Awaited<ReturnType<typeof getCompanyMembers>>>[number]
>;

/** Company shape rendered by companyMetadata: the getCompany result plus the
 *  derived silaWalletId field set in componentDidMount. */
type CompanyInfo = NonNullable<Awaited<ReturnType<typeof getCompany>>> & {
  silaWalletId?: string;
};

const actions = [adminConstants.REGISTER_KYB];
const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
  isFetching: loadingSelector(state),
  isSuperAdmin: isSuperIconAdmin(state),
});

const mapDispatchToProps = {
  registerKYB,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
  canEdit?: boolean;
  canSubmit?: boolean;
  // declared in the original PropTypes but never read in this file
  companyState?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  // present in initial state but never read; preserved verbatim
  companyInfo: Record<string, unknown>;
  // populated in componentDidMount, read in render
  company?: CompanyInfo;
  kybInfo: unknown;
  members: Member[];
  customError: string;
  isFetching: boolean;
  showConfirmationModal: boolean;
  targetBusinessMemberId: string | null;
  showConfirmAdvanceAlloyModal?: boolean;
  showKycStatusModal?: boolean;
  isDeleting?: boolean;
}

class Summary extends Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      companyInfo: {},
      kybInfo: {},
      members: [],
      customError: "",
      isFetching: true,
      showConfirmationModal: false,
      targetBusinessMemberId: null,
    };
  }

  async componentDidMount() {
    const client = this.props.client as ApolloClient<object>;
    const { companyInfo, members, kybInfo } =
      // @ts-expect-error Promise.props is a Bluebird runtime augmentation with
      // no TypeScript types in this project; the resolved object is untyped.
      await Promise.props({
        members: getCompanyMembers(client, this.props.companyId as string),
        companyInfo: getCompany(client, this.props.companyId as string),
        kybInfo: getCompanyKybData(client, this.props.companyId as string),
      });

    this.setState(
      {
        company: {
          ...companyInfo,
          silaWalletId: get(kybInfo, "silaWallet.accountNumber", ""),
        },
        members,
        kybInfo,
        isFetching: false,
        showConfirmAdvanceAlloyModal: false,
        targetBusinessMemberId: null,
      },
      () => this._validateMembers()
    );
  }

  _validateMembers = () => {
    const controllingOfficers = filter(this.state.members, (member) => {
      return (
        member.role === ControllingOfficer ||
        member.role === ControllingOfficerAndBeneficialOwner
      );
    });
    if (isEmpty(controllingOfficers)) {
      this.setState({ customError: "Company must have a controlling officer" });
      return false;
    }

    if (controllingOfficers.length > 1) {
      this.setState({
        customError: "Company must only have one controlling officer",
      });
      return false;
    }

    const stakeHolders = filter(this.state.members, (member) => {
      return (
        member.role === BeneficialOwner ||
        member.role === ControllingOfficerAndBeneficialOwner
      );
    });

    if (!isEmpty(stakeHolders)) {
      const numOfStakeHolders = stakeHolders.length;
      const totalStake = sumBy(stakeHolders, "ownershipStake");
      if (totalStake > 100) {
        this.setState({
          customError: "Ownership stake must not be greater than 100%",
        });
        return false;
      }

      const hasStakeHolderLessThan25 = some(stakeHolders, (stakeHolder) => {
        // ownershipStake is number | null; relies on JS null<25 coercion (null -> 0)
        return (stakeHolder.ownershipStake as unknown as number) < 25;
      });

      if (hasStakeHolderLessThan25) {
        this.setState({
          customError: "All stakeholders must have at least 25% stake",
        });
        return false;
      }

      if (numOfStakeHolders === 4 && totalStake !== 100) {
        this.setState({
          customError:
            "Ownership stake must be 100% if there are 4 stakeholders",
        });
        return false;
      }
    }

    return true;
  };

  submitCompany = async () => {
    let employer = {
      employerId: this.props.companyId as string,
    };

    this.props
      .registerKYB(this.props.client as ApolloClient<object>, employer)
      .then((result) => {
        if (!result.success) return;
        toast.success("Successfully submitted company for KYB.");
        this.props.push(`/employers/${this.props.companyId}`);
      })
      .catch((err: unknown) => {
        toast.error(wrapError(err));
      });
  };

  _buildOwnersSection() {
    const controllingOfficers = filter(this.state.members, (member) => {
      return (
        member.role === ControllingOfficer ||
        member.role === ControllingOfficerAndBeneficialOwner
      );
    });

    const hasOnlyOneControllingOfficer = controllingOfficers.length === 1;

    return map(this.state.members, (member) => {
      const isControllingOfficer =
        member.role === ControllingOfficer ||
        member.role === ControllingOfficerAndBeneficialOwner;

      const noRemoval = hasOnlyOneControllingOfficer && isControllingOfficer;

      const popover = (
        <Popover className="action-popover">
          <Popover.Body>
            {noRemoval && (
              <p>
                Cannot remove the only controlling officer. If removing due to
                an error then you must{" "}
                <Link
                  to={`/employers/${this.props.companyId}/kyb/owners`}
                  className="link"
                  style={{ paddingLeft: 0 }}
                >
                  add a new
                </Link>{" "}
                controlling officer before removing this one.
              </p>
            )}
            {!noRemoval && (
              <>
                <p>Are you sure you want to remove member?</p>
                <div>
                  <Button
                    type="button"
                    name="cancel"
                    color="cancel"
                    btnLabel="Cancel"
                    onClick={() => document.body.click()}
                    size="sm"
                  />
                  <Button
                    name="submit"
                    type="button"
                    color="red"
                    btnLabel="Remove"
                    withArrow
                    loading={this.state.isDeleting}
                    onClick={() => {
                      this.setState({ isDeleting: true });
                      removeCompanyMember(
                        this.props.client as ApolloClient<object>,
                        {
                          companyId: this.props.companyId as string,
                          memberId: member.id as string,
                        }
                      )
                        .then(() => {
                          return getCompanyMembers(
                            this.props.client as ApolloClient<object>,
                            this.props.companyId as string
                          ).then((members) => {
                            this.setState({
                              members: members as Member[],
                              isDeleting: false,
                            });
                            toast.success("Succesfully removed member.");
                          });
                        })
                        .catch(() => {
                          toast.error(
                            "Something went wrong while attempting to removing member"
                          );
                        });
                    }}
                    size="sm"
                  />
                </div>
              </>
            )}
          </Popover.Body>
        </Popover>
      );
      return (
        <div className="edit-wrapper">
          <InfoTable metadata={memberMetadata} data={member} />
          {this.props.canEdit && (
            <div className="action-items">
              {this._getAlloyYellowPathAdvanceAction(member)}
              <OverlayTrigger
                rootClose
                trigger="click"
                placement="bottom"
                overlay={popover}
              >
                <BsTrash className="trash-icon" size={20} color="#595A5C" />
              </OverlayTrigger>
              <BsPencilSquare
                className="edit-icon"
                size={20}
                color="#595A5C"
                onClick={() =>
                  this.props.push(
                    `/employers/${this.props.companyId}/kyb/owners?memberId=${member.id}`
                  )
                }
              />
            </div>
          )}
        </div>
      );
    });
  }

  _advanceAlloyJourney = async (member: Member) => {
    this.setState({
      showConfirmAdvanceAlloyModal: true,
      targetBusinessMemberId: member.id,
    });
  };

  _getAlloyYellowPathAdvanceAction = (member: Member) => {
    if (!this.props.isSuperAdmin) {
      return null;
    }

    if (member.state === "BusinessMemberKYCApproved") {
      return null;
    }

    return (
      <Button
        className="alloy-button"
        type="button"
        btnLabel="Advance Alloy Journey"
        name="submit"
        color="red"
        loading={false}
        onClick={() => this._advanceAlloyJourney(member)}
        size={"sm"}
        withArrow
      />
    );
  };

  _preValidateData = () => {
    const membersValid = this._validateMembers();

    if (membersValid) {
      this.setState({ showConfirmationModal: true });
    }
  };

  render() {
    if (this.state.isFetching) {
      return <IconSpinner centered />;
    }

    const canViewStatus = includes(
      [
        EmployerApplicationDenied,
        EmployerApplicationAdditionalDocumentationSubmitted,
        EmployerApplicationRequiresAdditionalDocumentation,
        EmployerApplicationSubmittedKYB,
      ],
      // company is always populated by componentDidMount before isFetching
      // flips false; assert non-null to preserve the original throw-on-undefined.
      this.state.company!.currentState
    );

    return (
      <>
        {this.state.showConfirmAdvanceAlloyModal && (
          <AdvanceAlloyConfirmationModal
            entityType="BUSINESS_MEMBER"
            entityId={this.state.targetBusinessMemberId as string}
            companyId={this.props.companyId}
            // @ts-expect-error AdvanceAlloyConfirmationModal is withApollo-wrapped,
            // so its external props omit `client`; the original JS passes the
            // parent's client explicitly (one app-wide client; Apollo honors it).
            client={this.props.client}
            onClose={() => {
              this.setState({ showConfirmAdvanceAlloyModal: false });
            }}
          />
        )}
        <div
          className="tab-pane"
          id="company"
          role="tabpanel"
          aria-labelledby="company_tab"
        >
          <div className="mega-container">
            {this.state.showKycStatusModal && (
              <KycJsonViewer
                companyId={this.props.companyId}
                onClose={() =>
                  this.setState({
                    showKycStatusModal: false,
                  })
                }
              />
            )}
            {this.state.showConfirmationModal && (
              <SimpleModal
                show={true}
                onClose={() => this.setState({ showConfirmationModal: false })}
                onSubmit={this.submitCompany}
                title="Submit KYB"
                buttonText="Submit"
                closeButtonText="Cancel"
                isXhrRequest={true}
                error={this.props.error}
                isFetching={this.props.isFetching}
                btnDisabled={this.props.isFetching}
              >
                Are you sure you want to submit company for KYB?
              </SimpleModal>
            )}
            <h4 className="page-title">Company</h4>
            <div className="edit-wrapper">
              <InfoTable metadata={companyMetadata} data={this.state.company} />
              {this.props.canEdit && (
                <div className="action-items">
                  {/* found-not-fixed: stray `qw` attribute is not a valid
                      react-icons prop (no-op at runtime); preserved verbatim. */}
                  <BsPencilSquare
                    className="edit-icon"
                    size={20}
                    // @ts-expect-error stray `qw` prop preserved verbatim (see above)
                    qw
                    color="#595A5C"
                    onClick={() =>
                      this.props.push(
                        `/employers/${this.props.companyId}/kyb/info`
                      )
                    }
                  />
                </div>
              )}
            </div>

            <h4 className="page-title">
              Controller and Beneficial Owners{" "}
              {this.props.canEdit && (
                <span style={{ fontSize: 16 }}>
                  ({" "}
                  <Link
                    to={`/employers/${this.props.companyId}/kyb/owners`}
                    className="link"
                    style={{ paddingLeft: 0 }}
                  >
                    Add More
                  </Link>{" "}
                  )
                </span>
              )}
            </h4>
            {this._buildOwnersSection()}
            {this.state.customError && (
              <Alert type={"error"} msg={this.state.customError} />
            )}
            <div className="submit-row">
              <Button
                name="submit"
                btnLabel="Submit for KYB"
                disabled={!this.props.canSubmit}
                onClick={() => this._preValidateData()}
              />
              <Button
                name="action"
                color="action"
                btnLabel="View KYB Status"
                disabled={!canViewStatus}
                onClick={() => this.setState({ showKycStatusModal: true })}
              />
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default connector(withApollo<Props>(Summary));
